import { Typography } from "@mui/material"
import { useTheme } from "@mui/material/styles"
import { ArrowDropDown as ArrowDownIcon, ArrowDropUp as ArrowUpIcon } from "@mui/icons-material"

import styled from "styled-components"
import React, { useRef } from "react"

type DeltaFormatterFunc = (delta: number) => string

const getColor = ({
  color,
  palette,
  value
}: {
  color: string
  palette: any
  value: number
}) => {
  switch (color.valueOf()) {
    case "positive":
      return palette.primary.main
    case "negative":
      return palette.error.main
    case "dynamic":
      return value < 0 ? palette.error.main : palette.primary.main
    default:
      return palette.text.primary
  }
}

const defaultDeltaFormatter = (delta: number) => {
  if (!delta) {
    return ""
  } else if (delta > 0) {
    return `${delta}`
  } else {
    return `${delta}`
  }
}

type IMetricProps = {
  /**
   * Never allow child elements
   */
  children?: never
  /**
   * @default "default"
   * A colour variant to give the component a green/red streak
   */
  color: "positive" | "negative" | "default" | "dynamic"
  /**
   * The change value to be shown as a small superscript addition
   * @default 0
   */
  delta?: number
  /**
   * Suffix to be applied to the delta value. Example: '%'
   * @default ""
   */
  deltaSuffix?: string
  /**
   * @default defaultDeltaFormatter
   * A custom function to transform the delta value into a string
   */
  deltaFormatter: DeltaFormatterFunc
  /**
   * An icon component to be rendered above the main value. Material UI icon components are recommended
   * @default ""
   */
  icon?: React.FunctionComponent<any>
  /**
   * The title to be shown above the main value
   * @default ""
   */
  title?: string
  /**
   * The description to be shown underneath the main value
   * @default ""
   */
  description?: string
  /**
   * Prefix to be applied to the main value. Example: '$'
   * @default ""
   */
  prefix?: string
  /**
   * Optional click handler
   */
  onClick?: React.EventHandler<any>
  /**
   * Suffix to be applied to the main value. Example: 'K'
   * @default ""
   */
  suffix?: string
  /**
   * @default 0
   * The main numeric value to be shown in the component
   */
  value: number
}

// type TRefAnim = {
//   current: null | Tween
// }

const MetricWrap = styled.div`
  width: 300px;
  display: grid;
  grid-template-areas:
    "icon header"
    "icon main-left"
    "icon footer";
  grid-template-columns: auto 1fr;
`
const IconWrap = styled.div`
  height: 100%; 
  margin-right: 0.5rem;
  display: flex;
  justify-content: center;
  align-items: flex-start;

  > svg {
    margin: auto;
  }
  
`
const MetricHeader = styled.div`
  padding-bottom: 0.5rem;
`
const MetricValueWrap = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;

  
`
const MetricFooter = styled.div`
`
const MetricDetailsWrap = styled.div<{ deltaColor: string }>`
  padding-left: 1rem;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  align-self: flex-start;

  svg {
    margin-top: 2px;

    > path {
      fill: ${({ deltaColor }) => deltaColor};
    }
  }
  .MuiTypography-h5 {
    color:  ${({ deltaColor }) => deltaColor};
  }
`

export const Metric = ({
  color = "default",
  delta = 0,
  deltaSuffix = "",
  deltaFormatter = defaultDeltaFormatter,
  icon: IconComponent,
  title = "",
  description = "",
  onClick,
  prefix = "",
  suffix = "",
  value = 0
}: IMetricProps) => {

  const theme = useTheme()

  const isDeltaPositive = delta > 0
  const deltaColor = isDeltaPositive ? theme.palette.success.main : theme.palette.error.main
  const deltaIcon = isDeltaPositive ? <ArrowUpIcon /> : <ArrowDownIcon />

  const variantColor = getColor({
    color,
    palette: theme.palette,
    value
  })

  const extraProps = onClick && typeof onClick === "function" ? { onClick } : {}

  return (
    <MetricWrap {...extraProps}>
      <MetricHeader style={{ gridArea: "header" }}>
        <Typography variant="h4">{title}</Typography>
      </MetricHeader>
      <MetricValueWrap style={{ gridArea: "main-left" }}>
        {!!IconComponent && (
          <IconWrap>
            <IconComponent
              style={{
                color: variantColor,
                width: 36,
                height: 36
              }}
            />
          </IconWrap>
        )}
        <div>
          {prefix ? <Typography component="span">{prefix}</Typography> : ""}
          <Typography variant="h1" component="span">
            {value}
          </Typography>
          {suffix ? <Typography component="span">{suffix}</Typography> : ""}
        </div>
        <MetricDetailsWrap style={{ gridArea: "main-right" }} deltaColor={deltaColor}>
          {delta ? deltaIcon : ''}
          <Typography variant="h5" component="span">
            {deltaFormatter(delta)}
            {deltaSuffix}
          </Typography>
        </MetricDetailsWrap>
      </MetricValueWrap>
      <MetricFooter style={{ gridArea: "footer" }}>
        <Typography variant="subtitle2">{description}</Typography>
      </MetricFooter>
    </MetricWrap>
  )
}

export default Metric
